import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApiService } from 'core/api';
import { ConfigService } from 'core/config';
import { BroadcastChannelService } from 'core/services';
import { catchError, combineLatest, filter, map, of, switchMap, take, tap } from 'rxjs';
import { designerPaths } from '../designer.paths';
import { JwtService } from './jwt.service';

@Injectable({ providedIn: 'root' })
export class JwtRefreshService {

  readonly jwtExpiredSubject = this._jwtService.jwtExpiredSubject;
  readonly jwtSubject = this._jwtService.jwtSubject;
  readonly refreshTokenExistsSubject = this._jwtService.refreshTokenExistsSubject;
  get refreshTokenExists() {
    return this.refreshTokenExistsSubject.value
  }

  readonly loggedInObs = combineLatest([this.jwtSubject, this.jwtExpiredSubject, this.refreshTokenExistsSubject])
    .pipe(
      switchMap(([jwt, jwtExpired, refreshTokenExists]) => {
        if (jwt && !jwtExpired) {
          return of(true);
        }

        if (refreshTokenExists !== false) {
          return this.refreshJwt().pipe(take(1))
        }

        return of(false)
      }),
    )

  get #isOidc() {
    return this._configService.appConfig.designerLogin === 'oidc'
  }

  readonly sessionExpiredSubject = this._broadcastChannelService.createBroadcastSubject<boolean>('sessionExpired');

  constructor(

    private readonly _configService: ConfigService,
    private readonly _jwtService: JwtService,
    private readonly _apiService: ApiService,
    private readonly _router: Router,
    private readonly _broadcastChannelService: BroadcastChannelService
  ) {

  }

  init() {

    this._jwtService.activityOccuredSubject
      .pipe(filter(x => x === false))
      .subscribe(() => {
        this.sessionExpiredSubject.next(true)
      })


    this.sessionExpiredSubject.subscribe(() => {
      this.#navigateToSessionExpired();
    })
  }

  readonly #handleLogout = (navigateTo: string[]) => ({ logoutUrl }: { logoutUrl?: string }) => {
    this.refreshTokenExistsSubject.next(false);
    this.jwtSubject.next(undefined);
    this._jwtService.reset();

    if (logoutUrl && this.#isOidc) {
      window.location.href = logoutUrl;
      return;
    }

    this._router.navigate(navigateTo);
  }

  logout(navigateTo: string[] = [designerPaths.auth]) {

    const url = 'activity/auth/refreshToken';

    return this._apiService.delete<{ logoutUrl?: string }>(url, undefined)
      .pipe(
        tap({
          error: this.#handleLogout(navigateTo),
          next: this.#handleLogout(navigateTo)
        }),
        catchError(() => of(null)),
      )
  }

  refreshJwt() {

    const url = 'activity/auth/refreshTokenFromCookie';


    return this._apiService.get<{ jwtToken: string }>(url, undefined, { withCredentials: true }).pipe(
      take(1),
      map(({ jwtToken }) => {
        this.jwtSubject.next(jwtToken);
        this.refreshTokenExistsSubject.next(true)
        return Boolean(jwtToken);
      }),
      catchError((error) => {

        return this.refreshTokenExistsSubject.pipe(
          take(1),
          switchMap((exists) => {
            if (!exists) {
              return of(false)
            }

            this.refreshTokenExistsSubject.next(false);
            this.jwtSubject.next(undefined);
            this._jwtService.reset();
            // return this.#openLoginExpiredDialog()
          }),
        )
      }),
    )
  }

  #navigateToSessionExpired() {
    this.logout([designerPaths.sessionExpired]).pipe(
      take(1)
    )
      .subscribe()


  }
}

